import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Badge, UncontrolledTooltip } from 'reactstrap';
import { AdminAgentDealUpdateRequestDto } from '@repo/common-types';
import { ColumnAlignment } from '@/common/tableData/DataTable';
import AgentDealUpdateRequestQuickActions from './components/AgentDealUpdateRequestQuickActions';
import { format } from 'date-fns';

export const createColumns = (
  onApply: (u: AdminAgentDealUpdateRequestDto) => void,
  onDelete: (u: AdminAgentDealUpdateRequestDto) => void,
  onEdit: (u: AdminAgentDealUpdateRequestDto) => void,
): ColumnDef<AdminAgentDealUpdateRequestDto>[] => [
  {
    id: 'actions',
    header: 'Actions',
    size: 80,
    cell: ({ row }) => {
      const update = row.original;

      return (
        <AgentDealUpdateRequestQuickActions
          update={update}
          onApply={() => onApply(update)}
          onEdit={() => onEdit(update)}
          onDelete={() => onDelete(update)}
        />
      );
    },
    meta: {
      alignment: ColumnAlignment.CENTER,
    },
    enableSorting: false,
  },
  {
    header: 'User',
    accessorKey: 'userId',
    size: 240,
    cell: ({ row }) => {
      const u = row.original;
      const alias = u.user?.alias || '';
      const email = u.user?.email || '';
      const tooltipId = `user-${u.id}`;

      return (
        <>
          <div className="d-flex flex-column" id={tooltipId}>
            <span className="fw-medium text-truncate" style={{ maxWidth: '200px' }}>
              {alias}
            </span>
            <small className="text-muted text-truncate" style={{ maxWidth: '200px' }}>
              {email}
            </small>
          </div>
          {(alias || email) && (
            <UncontrolledTooltip placement="top" target={tooltipId}>
              <div>
                {alias && <div>{alias}</div>}
                {email && <div>{email}</div>}
              </div>
            </UncontrolledTooltip>
          )}
        </>
      );
    },
  },
  {
    header: 'Deal',
    accessorKey: 'agentDealId',
    size: 140,
    cell: ({ row }) => <span className="text-muted">{row.original.agentDealId.slice(0, 8)}...</span>,
  },
  {
    header: 'Current %',
    accessorKey: 'currentPercentage',
    size: 110,
    cell: ({ row }) => <span className="fw-medium">{row.original.currentPercentage}%</span>,
    meta: {
      alignment: ColumnAlignment.RIGHT,
    },
  },
  {
    header: 'New %',
    accessorKey: 'newPercentage',
    size: 110,
    cell: ({ row }) => <span className="fw-medium">{row.original.newPercentage}%</span>,
    meta: {
      alignment: ColumnAlignment.RIGHT,
    },
  },
  {
    header: 'Cycle',
    accessorKey: 'currentCycle',
    size: 140,
    cell: ({ row }) => {
      const date = new Date(row.original.currentCycle);
      return <span>{format(date, 'yyyy-MM-dd')}</span>;
    },
  },
  {
    header: 'Valid From',
    accessorKey: 'validFrom',
    size: 140,
    cell: ({ row }) => {
      const date = new Date(row.original.validFrom);
      return <span>{format(date, 'yyyy-MM-dd')}</span>;
    },
  },
  {
    header: 'Status',
    accessorKey: 'applied',
    size: 120,
    cell: ({ row }) =>
      row.original.applied ? <Badge color="secondary">Applied</Badge> : <Badge color="warning">Pending</Badge>,
  },
  {
    header: 'Created By',
    accessorKey: 'createdBy',
    size: 240,
    cell: ({ row }) => {
      const u = row.original;
      const alias = u.createdByUser?.alias || '';
      const email = u.createdByUser?.email || '';
      const tooltipId = `createdBy-${u.id}`;

      return (
        <>
          <div className="d-flex flex-column" id={tooltipId}>
            <span className="fw-medium text-truncate" style={{ maxWidth: '200px' }}>
              {alias}
            </span>
            <small className="text-muted text-truncate" style={{ maxWidth: '200px' }}>
              {email}
            </small>
          </div>
          {(alias || email) && (
            <UncontrolledTooltip placement="top" target={tooltipId}>
              <div>
                {alias && <div>{alias}</div>}
                {email && <div>{email}</div>}
              </div>
            </UncontrolledTooltip>
          )}
        </>
      );
    },
  },
];
